import { useEffect, useRef, useState } from "react";
import { Sparkles, Gift } from "lucide-react";

/**
 * ScratchCard — canvas-based scratch-to-reveal interaction.
 *
 * Behavior:
 *   - Renders a shimmering gold "SCRATCH ME!" cover over a hidden reward.
 *   - User drags with finger/mouse to erase the cover (destination-out compositing).
 *   - When ~50% of the cover is erased, the card auto-completes, animates the
 *     remaining cover out, and calls `onReveal()` exactly once.
 *   - Reveal call is idempotent and pointer-safe against double-fire.
 *
 * Props:
 *   revealed:  boolean — if true, cover is skipped entirely (already scratched)
 *   awarded:   number  — the bonus amount to display when revealed
 *   coinType:  string  — 'gifto' | 'silver' | 'golden' (default 'gifto')
 *   onReveal:  fn      — invoked once when user hits the scratch threshold
 *   loading:   boolean — disables interaction while parent is awarding
 */
const COIN_LABEL = { gifto: "Gifto Coins", silver: "Silver Coins", golden: "Golden Coins" };
const COIN_ACCENT = {
  gifto: "from-pink-200 via-magenta-300 to-magenta-500",
  silver: "from-slate-100 via-slate-300 to-slate-500",
  golden: "from-yellow-100 via-yellow-300 to-yellow-500",
};

export default function ScratchCard({ revealed = false, awarded = null, coinType = "gifto", onReveal, loading = false }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [scratching, setScratching] = useState(false);
  const [progress, setProgress] = useState(0);
  const revealedRef = useRef(revealed);

  // Paint the gold "scratch me" cover once on mount.
  useEffect(() => {
    if (revealed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    // Gold gradient cover
    const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    grad.addColorStop(0, "#F09BB3");
    grad.addColorStop(0.4, "#E87A9C");
    grad.addColorStop(1, "#C24B6E");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);
    // Sparkle pattern
    ctx.fillStyle = "rgba(255,255,255,0.28)";
    for (let i = 0; i < 25; i++) {
      const x = (i * 47) % rect.width;
      const y = (i * 73) % rect.height;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    // Instruction label
    ctx.fillStyle = "#78350f";
    ctx.font = "bold 20px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("✋ SCRATCH ME!", rect.width / 2, rect.height / 2 - 6);
    ctx.font = "500 12px system-ui, sans-serif";
    ctx.fillText("Drag to reveal your bonus", rect.width / 2, rect.height / 2 + 16);
  }, [revealed]);

  const scratchAt = (clientX, clientY) => {
    if (revealedRef.current || loading) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 24, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    // Sample every ~10 strokes to estimate erase %
    if (Math.random() < 0.15) {
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let cleared = 0;
      const step = 64; // sample every N pixels for speed
      for (let i = 3; i < img.length; i += step) if (img[i] === 0) cleared++;
      const pct = (cleared / (img.length / step)) * 100;
      setProgress(pct);
      if (pct > 45 && !revealedRef.current) {
        revealedRef.current = true;
        // Fade out the remaining cover
        if (overlayRef.current) overlayRef.current.style.opacity = "0";
        setTimeout(() => onReveal && onReveal(), 350);
      }
    }
  };

  const onPointerDown = (e) => {
    setScratching(true);
    scratchAt(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!scratching) return;
    scratchAt(e.clientX, e.clientY);
  };
  const onPointerUp = () => setScratching(false);

  const label = COIN_LABEL[coinType] || "Coins";
  const accent = COIN_ACCENT[coinType] || COIN_ACCENT.gifto;

  return (
    <div className="relative w-full aspect-[2/1] rounded-2xl overflow-hidden shadow-xl border-4 border-white/40 select-none" data-testid="scratch-card">
      {/* Reward layer — always rendered underneath */}
      <div className={`absolute inset-0 bg-gradient-to-br ${accent} flex flex-col items-center justify-center text-center px-4`}>
        <Gift className="w-8 h-8 text-white mb-1" strokeWidth={1.8} />
        {loading ? (
          <>
            <div className="w-10 h-10 border-3 border-white/40 border-t-white rounded-full animate-spin mt-1" />
            <p className="text-xs text-white/90 mt-2 font-semibold">Revealing your bonus…</p>
          </>
        ) : awarded != null ? (
          <>
            <p className="text-[10px] uppercase tracking-widest text-white/80 font-bold">You Won</p>
            <p className="text-4xl font-bold text-white tabular-nums drop-shadow" data-testid="scratch-card-amount">+{awarded}</p>
            <p className="text-sm text-white/95 font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" />{label}</p>
          </>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Ready to Win</p>
            <p className="text-2xl font-bold text-white">Up to 100</p>
            <p className="text-xs text-white/95">{label}</p>
          </>
        )}
      </div>

      {/* Scratchable cover — hidden if already revealed */}
      {!revealed && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none transition-opacity duration-300 cursor-grab active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="scratch-card-canvas"
          style={{ opacity: 1 }}
        />
      )}
      {/* Overlay ref target used for fade-out on complete */}
      <div ref={overlayRef} className="pointer-events-none" />
      {!revealed && progress > 0 && (
        <div className="absolute bottom-1 left-0 right-0 h-1 bg-black/10">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
      )}
    </div>
  );
}
